import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import NavigationSidebar from './components/NavigationSidebar';
import ContentSection from './components/ContentSection';
import ExpandableCard from './components/ExpandableCard';
import SearchBar from './components/SearchBar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ContactButton from '../../components/ContactButton';

const HowItWorksPage = () => {
  const location = useLocation();
  const [activeSection, setActiveSection] = useState('overview');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [bookmarkedSections, setBookmarkedSections] = useState(['acute-chronic', 'hooper-index']);

  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll('section[id]');
      let currentSection = 'overview';

      sections?.forEach((section) => {
        const rect = section?.getBoundingClientRect();
        if (rect?.top <= 100 && rect?.bottom >= 100) {
          currentSection = section?.id;
        }
      });

      setActiveSection(currentSection);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleSectionClick = (sectionId) => {
    scrollToSection(sectionId);
    setActiveSection(sectionId);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const toggleBookmark = (sectionId) => {
    setBookmarkedSections((prev) =>
    prev?.includes(sectionId) ?
    prev?.filter((id) => id !== sectionId) :
    [...prev, sectionId]
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="flex pt-16">
        <NavigationSidebar
          activeSection={activeSection}
          onSectionClick={handleSectionClick}
          isMobileMenuOpen={isMobileMenuOpen}
          onMobileMenuClose={() => setIsMobileMenuOpen(false)} />


        <main className="flex-1 min-h-screen pb-24">
          {/* Mobile Header */}
          <div className="md:hidden bg-surface border-b border-border p-4">
            <div className="flex items-center justify-between mb-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsMobileMenuOpen(true)}>

                <Icon name="Menu" size={20} />
                Topics
              </Button>
              <div className="flex items-center space-x-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => toggleBookmark(activeSection)}>

                  <Icon
                    name={bookmarkedSections?.includes(activeSection) ? "Bookmark" : "BookmarkPlus"}
                    size={18}
                    color={bookmarkedSections?.includes(activeSection) ? "var(--color-warning)" : "currentColor"} />

                </Button>
              </div>
            </div>
            <SearchBar onSearch={handleSearch} />
          </div>

          <div className="p-6 max-w-4xl mx-auto">
            {/* Desktop Header */}
            <div className="hidden md:block mb-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h1 className="text-3xl font-bold text-text-primary mb-2">
                    How It Works
                  </h1>
                  <p className="text-text-secondary">
                    Learn about load recovery methodology and effective platform utilization
                  </p>
                </div>
              </div>
              <SearchBar onSearch={handleSearch} />
            </div>

            {/* Getting Started Section */}
            <ContentSection id="overview" title="Platform Overview" icon="Compass">
              <div className="space-y-4">
                <p className="text-text-secondary leading-relaxed">
                  Sports Science Platform is a comprehensive sports analytics platform designed to help athletes optimize training through data-driven insights. Our system combines real-time training load monitoring with daily wellness tracking to provide actionable recommendations for performance enhancement and injury prevention.
                </p>
                
                <div className="bg-surface border border-primary/20 rounded-lg p-4 mb-4">
                  <div className="flex items-start space-x-3">
                    <Icon name="Info" size={20} color="var(--color-primary)" className="mt-0.5 flex-shrink-0" strokeWidth={2} />
                    <div>
                      <h4 className="font-semibold text-text-primary mb-1">Getting Started: Build Your Data Foundation</h4>
                      <p className="text-sm text-text-secondary mb-2">
                        For optimal results, aim to complete 28 days of consistent data entry. This allows the system to establish accurate baselines for your training capacity and wellness patterns.
                      </p>
                      <p className="text-sm text-text-secondary">
                        <strong>Don't worry:</strong> The platform is functional from day one and will provide increasingly accurate insights as your data history grows. Each day of data improves the quality of your personalized recommendations.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-muted rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <Icon name="BarChart3" size={20} color="var(--color-primary)" strokeWidth={2} />
                      <h4 className="font-semibold text-text-primary">Dashboard Analytics</h4>
                    </div>
                    <p className="text-sm text-text-secondary">
                      Real-time visualization of your Load Ratio, Total Wellness Score, and Readiness Index with color-coded status indicators and trend analysis.
                    </p>
                  </div>
                  
                  <div className="bg-muted rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <Icon name="Heart" size={20} color="var(--color-accent)" strokeWidth={2} />
                      <h4 className="font-semibold text-text-primary">Daily Wellness Tracking</h4>
                    </div>
                    <p className="text-sm text-text-secondary">
                      Monitor daily wellness using the validated Hooper Index with four key components: sleep quality, fatigue levels, muscle soreness, and stress levels.
                    </p>
                  </div>
                  
                  <div className="bg-muted rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <Icon name="TrendingUp" size={20} color="var(--color-success)" strokeWidth={2} />
                      <h4 className="font-semibold text-text-primary">Readiness Index</h4>
                    </div>
                    <p className="text-sm text-text-secondary">
                      Combined metric integrating your training load and wellness data to provide clear training recommendations based on your current state.
                    </p>
                  </div>
                </div>
              </div>
            </ContentSection>

            <ContentSection id="first-steps" title="Your First Steps" icon="Play">
              <div className="space-y-6">
                <div className="bg-accent/10 border border-accent/30 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <Icon name="Calendar" size={20} color="var(--color-accent)" className="mt-0.5 flex-shrink-0" strokeWidth={2} />
                    <div>
                      <h4 className="font-semibold text-text-primary mb-2">The 28-Day Journey to Full Functionality</h4>
                      <p className="text-sm text-text-secondary mb-2">
                        While you can start using the platform immediately, the system reaches its full analytical power after 28 days of consistent data entry. Here's why:
                      </p>
                      <ul className="text-sm text-text-secondary space-y-1">
                        <li>• <strong>Chronic Load Calculation:</strong> Requires 28 days of training data for accurate baseline</li>
                        <li>• <strong>Wellness Patterns:</strong> Needs 28 days to identify your personal trends and establish normal ranges</li>
                        <li>• <strong>Readiness Index:</strong> Becomes most accurate after establishing your individual baselines</li>
                      </ul>
                      <div className="mt-3 p-3 bg-background rounded">
                        <p className="text-xs text-text-secondary">
                          <Icon name="Lightbulb" size={12} color="var(--color-warning)" className="inline mr-1" />
                          <strong>Good news:</strong> The platform provides useful insights from day one and becomes more personalized as your data grows. Don't wait – start logging today!
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <ExpandableCard
                  title="1. Log Your First Training Session"
                  summary="Start building your training load history with RPE and duration tracking"
                  icon="Plus"
                  defaultExpanded={true}>

                  <div className="space-y-3">
                    <p className="text-text-secondary">
                      Begin by logging your first training session directly on the dashboard. Each session requires an RPE (Rate of Perceived Exertion) score, duration, activity type, and timing details. This data automatically calculates your training load and updates the live progression chart.
                    </p>
                    
                    <div className="bg-muted rounded-lg p-4 mb-3">
                      <h5 className="font-medium text-text-primary mb-2">Why Log Training Sessions?</h5>
                      <ul className="text-sm text-text-secondary space-y-1">
                        <li>• <strong>Track Training Stress:</strong> Understand the cumulative impact of your training</li>
                        <li>• <strong>Prevent Overtraining:</strong> Identify when you're pushing too hard relative to your capacity</li>
                        <li>• <strong>Optimize Progression:</strong> Make data-driven decisions about when to increase or reduce load</li>
                        <li>• <strong>Monitor Patterns:</strong> See how different activities contribute to your overall training stress</li>
                      </ul>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-surface border border-border rounded-lg p-3">
                        <h5 className="font-medium text-text-primary mb-2">RPE Scale (1-10)</h5>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-text-secondary">1-2:</span>
                            <span>Very Easy</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-text-secondary">3-4:</span>
                            <span>Easy</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-text-secondary">5-6:</span>
                            <span>Moderate</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-text-secondary">7-8:</span>
                            <span>Hard</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-text-secondary">9-10:</span>
                            <span>Very Hard</span>
                          </div>
                        </div>
                      </div>
                      <div className="bg-surface border border-border rounded-lg p-3">
                        <h5 className="font-medium text-text-primary mb-2">What You Get</h5>
                        <ul className="text-sm text-text-secondary space-y-1">
                          <li>• Training load calculated automatically</li>
                          <li>• Live chart updates with your new data</li>
                          <li>• Load ratio recalculated in real-time</li>
                          <li>• Risk band status updated immediately</li>
                          <li>• Session appears in history table</li>
                          <li>• Activity type tracking for pattern analysis</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </ExpandableCard>

                <ExpandableCard
                  title="2. Complete Your First Wellness Check"
                  summary="Navigate to Daily Wellness and establish baseline wellness metrics"
                  icon="Heart">

                  <div className="space-y-3">
                    <p className="text-text-secondary">
                      Visit the Daily Wellness page to complete your first Hooper Index assessment. Rate each of the four components on a 1-7 scale to establish your wellness baseline and start building your wellness trend data.
                    </p>
                    
                    <div className="bg-muted rounded-lg p-4 mb-3">
                      <h5 className="font-medium text-text-primary mb-2">Why Track Daily Wellness?</h5>
                      <ul className="text-sm text-text-secondary space-y-1">
                        <li>• <strong>Early Warning System:</strong> Detect signs of overtraining or inadequate recovery before they impact performance</li>
                        <li>• <strong>Recovery Monitoring:</strong> Understand how well your body is adapting to training stress</li>
                        <li>• <strong>Training Adjustments:</strong> Make informed decisions about when to push hard or back off</li>
                        <li>• <strong>Pattern Recognition:</strong> Identify which factors most affect your readiness to train</li>
                      </ul>
                    </div>

                    <div className="bg-muted rounded-lg p-4">
                      <h5 className="font-medium text-text-primary mb-2">Four Wellness Components:</h5>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="flex items-center space-x-2">
                          <Icon name="Moon" size={16} color="var(--color-primary)" />
                          <span className="text-text-secondary">Sleep Quality (1-7)</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Icon name="Battery" size={16} color="var(--color-primary)" />
                          <span className="text-text-secondary">Fatigue Levels (1-7)</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Icon name="Zap" size={16} color="var(--color-primary)" />
                          <span className="text-text-secondary">Muscle Soreness (1-7)</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Icon name="Brain" size={16} color="var(--color-primary)" />
                          <span className="text-text-secondary">Stress Levels (1-7)</span>
                        </div>
                      </div>
                      <p className="text-xs text-text-secondary mt-3">
                        <strong>Total Wellness Score:</strong> Ranges from 4 (worst) to 28 (best), automatically calculated from your four component ratings
                      </p>
                    </div>
                  </div>
                </ExpandableCard>

                <ExpandableCard
                  title="3. Understanding Your Dashboard Metrics"
                  summary="Learn to interpret Load Ratio, Wellness Score, and Readiness Index"
                  icon="BarChart3">

                  <div className="space-y-4">
                    <p className="text-text-secondary">
                      Your dashboard displays three key metrics that work together to provide a complete picture of your training status and recovery state. Each metric serves a specific purpose in helping you optimize training.
                    </p>

                    <div className="space-y-4">
                      {/* Load Ratio */}
                      <div className="bg-surface border border-border rounded-lg p-4">
                        <div className="flex items-center space-x-2 mb-3">
                          <Icon name="Activity" size={20} color="var(--color-primary)" strokeWidth={2} />
                          <h5 className="font-semibold text-text-primary">Load Ratio (ACWR)</h5>
                        </div>
                        <p className="text-sm text-text-secondary mb-3">
                          Compares your recent training load (last 7 days) to your long-term training capacity (last 28 days). This ratio helps identify if you're training appropriately for your current fitness level.
                        </p>
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <div className="w-3 h-3 rounded" style={{ backgroundColor: '#16A34A' }}></div>
                            <span className="text-xs text-text-secondary">Optimal Zone (0.8-1.3) - Ideal for adaptation</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div className="w-3 h-3 rounded" style={{ backgroundColor: '#D97706' }}></div>
                            <span className="text-xs text-text-secondary">Moderate Risk (1.3-1.5) - Monitor closely</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div className="w-3 h-3 rounded" style={{ backgroundColor: '#DC2626' }}></div>
                            <span className="text-xs text-text-secondary">High Risk (&gt;1.5) - Reduce training load</span>
                          </div>
                        </div>
                        <div className="mt-3 p-3 bg-muted rounded">
                          <p className="text-xs text-text-secondary">
                            <strong>What You Get:</strong> Color-coded risk bands, percentage change from yesterday, and immediate visual feedback on your training status
                          </p>
                        </div>
                      </div>

                      {/* Wellness Score */}
                      <div className="bg-surface border border-border rounded-lg p-4">
                        <div className="flex items-center space-x-2 mb-3">
                          <Icon name="Heart" size={20} color="var(--color-accent)" strokeWidth={2} />
                          <h5 className="font-semibold text-text-primary">Total Wellness Score</h5>
                        </div>
                        <p className="text-sm text-text-secondary mb-3">
                          Your daily wellness total (4-28 scale) based on the Hooper Index methodology. This validated system combines sleep quality, fatigue, muscle soreness, and stress into a single readiness indicator.
                        </p>
                        <div className="grid grid-cols-2 gap-3 text-xs">
                          <div className="bg-muted rounded p-2">
                            <strong className="text-text-primary">Score 22-28:</strong>
                            <p className="text-text-secondary">Excellent wellness, ready for high-intensity training</p>
                          </div>
                          <div className="bg-muted rounded p-2">
                            <strong className="text-text-primary">Score 18-21:</strong>
                            <p className="text-text-secondary">Good wellness, suitable for moderate training</p>
                          </div>
                          <div className="bg-muted rounded p-2">
                            <strong className="text-text-primary">Score 14-17:</strong>
                            <p className="text-text-secondary">Moderate wellness, consider easier training</p>
                          </div>
                          <div className="bg-muted rounded p-2">
                            <strong className="text-text-primary">Score 4-13:</strong>
                            <p className="text-text-secondary">Poor wellness, prioritize recovery</p>
                          </div>
                        </div>
                        <div className="mt-3 p-3 bg-muted rounded">
                          <p className="text-xs text-text-secondary">
                            <strong>What You Get:</strong> Daily wellness total, breakdown by component (sleep, fatigue, soreness, stress), trend comparison with yesterday, and 30-day pattern visualization
                          </p>
                        </div>
                      </div>

                      {/* Readiness Index */}
                      <div className="bg-surface border border-border rounded-lg p-4">
                        <div className="flex items-center space-x-2 mb-3">
                          <Icon name="Target" size={20} color="var(--color-success)" strokeWidth={2} />
                          <h5 className="font-semibold text-text-primary">Readiness Index</h5>
                        </div>
                        <p className="text-sm text-text-secondary mb-3">
                          A comprehensive metric that combines your Load Ratio (training stress) and Wellness Score (recovery status) to provide clear, actionable training recommendations. This is your "go/no-go" indicator for today's training.
                        </p>
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <div className="w-3 h-3 rounded" style={{ backgroundColor: '#16A34A' }}></div>
                            <span className="text-xs text-text-secondary">Green - Proceed with planned training</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div className="w-3 h-3 rounded" style={{ backgroundColor: '#D97706' }}></div>
                            <span className="text-xs text-text-secondary">Amber - Modify training intensity or volume</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div className="w-3 h-3 rounded" style={{ backgroundColor: '#DC2626' }}></div>
                            <span className="text-xs text-text-secondary">Red - Prioritize recovery, reduce training</span>
                          </div>
                        </div>
                        <div className="mt-3 p-3 bg-muted rounded">
                          <p className="text-xs text-text-secondary">
                            <strong>What You Get:</strong> Color-coded readiness band, breakdown of risk components (training load vs wellness), and specific text recommendations tailored to your current state (e.g., "Load and wellness are in line with your normal. Proceed with planned session.")
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-primary/10 border border-primary/30 rounded-lg p-4 mt-4">
                      <h5 className="font-semibold text-text-primary mb-2">How These Metrics Work Together</h5>
                      <p className="text-sm text-text-secondary mb-3">
                        Think of these three metrics as a traffic light system for your training:
                      </p>
                      <ul className="text-sm text-text-secondary space-y-2">
                        <li>• <strong>Load Ratio</strong> tells you if your recent training matches your fitness capacity</li>
                        <li>• <strong>Wellness Score</strong> tells you how recovered and ready you feel</li>
                        <li>• <strong>Readiness Index</strong> combines both to give you clear training guidance</li>
                      </ul>
                      <p className="text-xs text-text-secondary mt-3">
                        <Icon name="Info" size={12} color="var(--color-primary)" className="inline mr-1" />
                        The more data you log, the more personalized and accurate these recommendations become!
                      </p>
                    </div>
                  </div>
                </ExpandableCard>
              </div>
            </ContentSection>

            {/* Load Ratio Science Section */}
            <ContentSection id="acute-chronic" title="Acute vs Chronic Load" icon="TrendingUp">
              <div className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="bg-surface border border-border rounded-lg p-4">
                      <div className="flex items-center space-x-2 mb-3">
                        <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                          <Icon name="Zap" size={16} color="white" strokeWidth={2} />
                        </div>
                        <h4 className="font-semibold text-text-primary">Acute Load</h4>
                      </div>
                      <p className="text-sm text-text-secondary mb-3">
                        The average training load over the past 7 days, representing your recent training stress and current fitness adaptations.
                      </p>
                      <div className="bg-muted rounded p-3">
                        <p className="text-xs text-text-secondary mb-1">Calculation:</p>
                        <p className="font-mono text-sm text-text-primary">
                          Sum of (RPE × Duration) ÷ 7 days
                        </p>
                      </div>
                    </div>

                    <div className="bg-surface border border-border rounded-lg p-4">
                      <div className="flex items-center space-x-2 mb-3">
                        <div className="w-8 h-8 bg-accent rounded-lg flex items-center justify-center">
                          <Icon name="Calendar" size={16} color="white" strokeWidth={2} />
                        </div>
                        <h4 className="font-semibold text-text-primary">Chronic Load</h4>
                      </div>
                      <p className="text-sm text-text-secondary mb-3">
                        The average training load over the past 28 days, representing your baseline fitness level and training capacity.
                      </p>
                      <div className="bg-muted rounded p-3">
                        <p className="text-xs text-text-secondary mb-1">Calculation:</p>
                        <p className="font-mono text-sm text-text-primary">
                          Sum of (RPE × Duration) ÷ 28 days
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-muted rounded-lg p-4">
                    <h4 className="font-semibold text-text-primary mb-3">Load Ratio Significance</h4>
                    <p className="text-sm text-text-secondary mb-4">
                      The acute:chronic load ratio (ACWR) provides insight into your current training stress relative to your fitness capacity, helping identify optimal training zones and injury risk periods.
                    </p>
                    
                    <div className="space-y-3">
                      <div className="flex items-center space-x-3">
                        <div className="w-4 h-4 rounded" style={{ backgroundColor: '#059669' }}></div>
                        <div>
                          <p className="text-sm font-medium text-text-primary">&lt; 0.8 - Low Load</p>
                          <p className="text-xs text-text-secondary">Potential detraining risk</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <div className="w-4 h-4 rounded" style={{ backgroundColor: '#16A34A' }}></div>
                        <div>
                          <p className="text-sm font-medium text-text-primary">0.8 - 1.3 - Optimal</p>
                          <p className="text-xs text-text-secondary">Sweet spot for adaptation</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <div className="w-4 h-4 rounded" style={{ backgroundColor: '#D97706' }}></div>
                        <div>
                          <p className="text-sm font-medium text-text-primary">1.3 - 1.5 - Moderate Risk</p>
                          <p className="text-xs text-text-secondary">Monitor closely</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <div className="w-4 h-4 rounded" style={{ backgroundColor: '#DC2626' }}></div>
                        <div>
                          <p className="text-sm font-medium text-text-primary">&gt; 1.5 - High Risk</p>
                          <p className="text-xs text-text-secondary">Injury risk elevated</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </ContentSection>

            <ContentSection id="calculations" title="Load Calculations" icon="Calculator">
              <div className="space-y-6">
                <ExpandableCard
                  title="Training Load Formula"
                  summary="Understanding the basic calculation: RPE × Duration"
                  icon="Formula"
                  defaultExpanded={true}>

                  <div className="space-y-4">
                    <p className="text-text-secondary">
                      Training load is calculated using the session-RPE method, which multiplies the Rate of Perceived Exertion (RPE) by the session duration in minutes.
                    </p>
                    
                    <div className="bg-muted rounded-lg p-4">
                      <h5 className="font-medium text-text-primary mb-3">Example Calculation:</h5>
                      <div className="space-y-2 font-mono text-sm">
                        <div className="flex justify-between">
                          <span className="text-text-secondary">Session RPE:</span>
                          <span className="text-text-primary">7 (Hard)</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-text-secondary">Duration:</span>
                          <span className="text-text-primary">60 minutes</span>
                        </div>
                        <div className="border-t border-border pt-2 flex justify-between font-semibold">
                          <span className="text-text-secondary">Training Load:</span>
                          <span className="text-text-primary">7 × 60 = 420 AU</span>
                        </div>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-surface border border-border rounded-lg p-4">
                        <h5 className="font-medium text-text-primary mb-2">Weekly Load Calculation</h5>
                        <p className="text-sm text-text-secondary mb-3">
                          Sum all training loads from the past 7 days, then divide by 7 to get the acute load average.
                        </p>
                        <div className="text-xs font-mono bg-muted rounded p-2">
                          Acute Load = Σ(Daily Loads) ÷ 7
                        </div>
                      </div>
                      
                      <div className="bg-surface border border-border rounded-lg p-4">
                        <h5 className="font-medium text-text-primary mb-2">Monthly Load Calculation</h5>
                        <p className="text-sm text-text-secondary mb-3">
                          Sum all training loads from the past 28 days, then divide by 28 to get the chronic load average.
                        </p>
                        <div className="text-xs font-mono bg-muted rounded p-2">
                          Chronic Load = Σ(Daily Loads) ÷ 28
                        </div>
                      </div>
                    </div>
                  </div>
                </ExpandableCard>

                <ExpandableCard
                  title="RPE Guidelines"
                  summary="Detailed guidance for accurate RPE assessment"
                  icon="Target">

                  <div className="space-y-4">
                    <p className="text-text-secondary">
                      Accurate RPE assessment is crucial for meaningful load calculations. Use the Borg CR10 scale consistently across all training sessions.
                    </p>
                    
                    <div className="grid gap-3">
                      {[
                      { rpe: '1-2', intensity: 'Very Easy', description: 'Could maintain for hours, minimal effort', color: '#059669' },
                      { rpe: '3-4', intensity: 'Easy', description: 'Comfortable pace, can hold conversation', color: '#16A34A' },
                      { rpe: '5-6', intensity: 'Moderate', description: 'Somewhat hard, breathing elevated', color: '#0EA5E9' },
                      { rpe: '7-8', intensity: 'Hard', description: 'Difficult to maintain, heavy breathing', color: '#D97706' },
                      { rpe: '9-10', intensity: 'Very Hard', description: 'Maximal effort, unsustainable', color: '#DC2626' }]?.
                      map((item, index) =>
                      <div key={index} className="flex items-center space-x-4 p-3 bg-surface border border-border rounded-lg">
                          <div
                          className="w-12 h-8 rounded flex items-center justify-center text-white font-bold text-sm"
                          style={{ backgroundColor: item?.color }}>

                            {item?.rpe}
                          </div>
                          <div className="flex-1">
                            <h6 className="font-medium text-text-primary">{item?.intensity}</h6>
                            <p className="text-sm text-text-secondary">{item?.description}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </ExpandableCard>
              </div>
            </ContentSection>

            <ContentSection id="risk-bands" title="Risk Band System" icon="AlertTriangle">
              <div className="space-y-6">
                <p className="text-text-secondary leading-relaxed">
                  The risk band system provides immediate visual feedback about your current training status based on the acute:chronic load ratio. Each band represents different physiological states and injury risk levels.
                </p>

                <div className="grid gap-4">
                  {[
                  {
                    band: 'Low Load',
                    range: '< 0.8',
                    color: '#059669',
                    bgColor: '#ECFDF5',
                    icon: 'TrendingDown',
                    description: 'Current training load is significantly below your fitness capacity. While injury risk is low, there may be a risk of detraining or fitness loss.',
                    recommendations: [
                    'Consider gradually increasing training volume',
                    'Maintain current intensity but add duration',
                    'Monitor for signs of detraining over time']

                  },
                  {
                    band: 'Optimal Zone',
                    range: '0.8 - 1.3',
                    color: '#16A34A',
                    bgColor: '#F0FDF4',
                    icon: 'Target',
                    description: 'The sweet spot for training adaptations. Your current load is well-matched to your fitness capacity, promoting positive adaptations with minimal injury risk.',
                    recommendations: [
                    'Maintain current training approach',
                    'Focus on consistency and progression',
                    'Monitor wellness markers closely']

                  },
                  {
                    band: 'Moderate Risk',
                    range: '1.3 - 1.5',
                    color: '#D97706',
                    bgColor: '#FFFBEB',
                    icon: 'AlertCircle',
                    description: 'Training load is elevated relative to your fitness capacity. Increased monitoring is recommended as injury risk begins to rise.',
                    recommendations: [
                    'Monitor wellness scores daily',
                    'Consider reducing training volume',
                    'Prioritise recovery strategies']

                  },
                  {
                    band: 'High Risk',
                    range: '> 1.5',
                    color: '#DC2626',
                    bgColor: '#FEF2F2',
                    icon: 'AlertTriangle',
                    description: 'Significantly elevated injury risk. Current training load far exceeds your fitness capacity, requiring immediate attention and load reduction.',
                    recommendations: [
                    'Reduce training volume immediately',
                    'Focus on recovery and regeneration',
                    'Consider rest days or active recovery']

                  }]?.
                  map((risk, index) =>
                  <div key={index} className="bg-surface border border-border rounded-lg overflow-hidden">
                      <div
                      className="p-4 border-l-4"
                      style={{ borderLeftColor: risk?.color, backgroundColor: risk?.bgColor }}>

                        <div className="flex items-center space-x-3 mb-3">
                          <Icon name={risk?.icon} size={24} color={risk?.color} strokeWidth={2} />
                          <div>
                            <h4 className="text-lg font-semibold" style={{ color: risk?.color }}>
                              {risk?.band}
                            </h4>
                            <p className="text-sm font-mono" style={{ color: risk?.color }}>
                              Ratio: {risk?.range}
                            </p>
                          </div>
                        </div>
                        <p className="text-text-secondary mb-4">{risk?.description}</p>
                        
                        <div>
                          <h5 className="font-medium text-text-primary mb-2">Recommendations:</h5>
                          <ul className="space-y-1">
                            {risk?.recommendations?.map((rec, recIndex) =>
                          <li key={recIndex} className="flex items-start space-x-2 text-sm text-text-secondary">
                                <Icon name="ArrowRight" size={14} color="currentColor" className="mt-0.5 flex-shrink-0" />
                                <span>{rec}</span>
                              </li>
                          )}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </ContentSection>

            {/* Wellness Tracking Section */}
            <ContentSection id="hooper-index" title="Hooper Index Method" icon="Heart">
              <div className="space-y-6">
                <p className="text-text-secondary leading-relaxed">
                  The Hooper Index is a validated method for monitoring daily wellness in athletes. It uses four simple subjective measures rated on a 1-7 scale to provide insight into your body's readiness for training. The total score (ranging from 4 to 28) gives you an at-a-glance view of your recovery status.
                </p>

                <div className="bg-surface border border-border rounded-lg p-6">
                  <h4 className="font-semibold text-text-primary mb-4">Scientific Background</h4>
                  <div className="space-y-4">
                    <p className="text-text-secondary">
                      Developed by Dr. Sue Hooper, this method has been extensively validated in research with elite athletes across various sports. The four components provide a holistic view of physiological and psychological readiness.
                    </p>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-muted rounded-lg p-4">
                        <h5 className="font-medium text-text-primary mb-2">Research Evidence</h5>
                        <ul className="text-sm text-text-secondary space-y-1">
                          <li>• Validated across multiple sports</li>
                          <li>• Correlates with physiological markers</li>
                          <li>• Predicts performance readiness</li>
                          <li>• Simple and time-efficient</li>
                        </ul>
                      </div>
                      
                      <div className="bg-muted rounded-lg p-4">
                        <h5 className="font-medium text-text-primary mb-2">Practical Benefits</h5>
                        <ul className="text-sm text-text-secondary space-y-1">
                          <li>• Early overtraining detection</li>
                          <li>• Objective wellness tracking</li>
                          <li>• Training load adjustment guidance</li>
                          <li>• Recovery monitoring</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-accent/10 border border-accent/30 rounded-lg p-4">
                  <h4 className="font-semibold text-text-primary mb-2">Understanding Your Wellness Score Output</h4>
                  <p className="text-sm text-text-secondary mb-3">
                    Each day, the platform calculates your Total Wellness Score by summing your four component ratings. This score is displayed on your dashboard and tracked over time to show patterns and trends.
                  </p>
                  <div className="grid md:grid-cols-2 gap-3">
                    <div className="bg-background rounded p-3">
                      <p className="text-xs font-medium text-text-primary mb-1">On Your Dashboard:</p>
                      <ul className="text-xs text-text-secondary space-y-1">
                        <li>• Current day's total wellness score (out of 28)</li>
                        <li>• Percentage change from yesterday</li>
                        <li>• Individual component breakdown</li>
                        <li>• Color-coded status indicator</li>
                      </ul>
                    </div>
                    <div className="bg-background rounded p-3">
                      <p className="text-xs font-medium text-text-primary mb-1">On Wellness Page:</p>
                      <ul className="text-xs text-text-secondary space-y-1">
                        <li>• 30-day wellness trend chart</li>
                        <li>• Automated wellness insights</li>
                        <li>• Pattern analysis (7-day averages)</li>
                        <li>• Historical comparison data</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </ContentSection>

            {/* New Section: Readiness Index Explained */}
            <ContentSection id="readiness-index" title="Readiness Index Explained" icon="Target">
              <div className="space-y-6">
                <p className="text-text-secondary leading-relaxed">
                  The Readiness Index is your personalized training guidance system that combines training load analysis and wellness monitoring into a single, actionable recommendation. It answers the critical daily question: "Should I train hard today, or should I back off?"
                </p>

                <div className="bg-primary/10 border border-primary/30 rounded-lg p-4">
                  <h4 className="font-semibold text-text-primary mb-3">What The Readiness Index Tells You</h4>
                  <div className="space-y-3">
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-success rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon name="CheckCircle" size={16} color="white" strokeWidth={2} />
                      </div>
                      <div>
                        <p className="font-medium text-text-primary">Green Band (Low Risk)</p>
                        <p className="text-sm text-text-secondary">Your training load and wellness are well-balanced. You're ready for your planned training session without elevated injury risk.</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-warning rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon name="AlertCircle" size={16} color="white" strokeWidth={2} />
                      </div>
                      <div>
                        <p className="font-medium text-text-primary">Amber Band (Moderate Risk)</p>
                        <p className="text-sm text-text-secondary">Either your training load is elevated or your wellness is suppressed. Consider modifying today's session - reduce intensity or volume as needed.</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-error rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon name="AlertTriangle" size={16} color="white" strokeWidth={2} />
                      </div>
                      <div>
                        <p className="font-medium text-text-primary">Red Band (High Risk)</p>
                        <p className="text-sm text-text-secondary">You're training harder than usual while feeling worse than normal. Strongly consider reducing today's load and emphasizing recovery.</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-surface border border-border rounded-lg p-6">
                  <h4 className="font-semibold text-text-primary mb-4">How It Works (Without Revealing Calculations)</h4>
                  <p className="text-sm text-text-secondary mb-4">
                    The Readiness Index analyzes your recent training patterns and current wellness state, comparing them to your personal baselines established over the previous 28 days. This personalized approach ensures recommendations are tailored specifically to you, not generic guidelines.
                  </p>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-muted rounded-lg p-4">
                      <h5 className="font-medium text-text-primary mb-2">Training Load Component</h5>
                      <p className="text-sm text-text-secondary mb-3">
                        Evaluates whether your recent training is higher, lower, or normal compared to your typical training load. This helps identify if you're pushing too hard or potentially detraining.
                      </p>
                      <div className="text-xs bg-background rounded p-2">
                        <strong>States:</strong> Low load, Moderate load, or High spike
                      </div>
                    </div>
                    
                    <div className="bg-muted rounded-lg p-4">
                      <h5 className="font-medium text-text-primary mb-2">Wellness Component</h5>
                      <p className="text-sm text-text-secondary mb-3">
                        Assesses whether your current wellness is better, worse, or typical compared to your normal state. This accounts for how recovered and ready you feel.
                      </p>
                      <div className="text-xs bg-background rounded p-2">
                        <strong>States:</strong> Good, Typical, Suppressed, or Very poor
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-muted rounded-lg p-6">
                  <h4 className="font-semibold text-text-primary mb-4">What You Need To Do</h4>
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-white text-xs font-bold">1</span>
                      </div>
                      <div>
                        <p className="font-medium text-text-primary mb-1">Log Training Sessions Daily</p>
                        <p className="text-sm text-text-secondary">Enter your RPE and duration for each training session. This builds your training load history and establishes your personal capacity baseline.</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-white text-xs font-bold">2</span>
                      </div>
                      <div>
                        <p className="font-medium text-text-primary mb-1">Complete Wellness Check-ins Daily</p>
                        <p className="text-sm text-text-secondary">Rate your sleep, fatigue, soreness, and stress each morning. Consistency is key - aim for the same time each day for accurate comparisons.</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-white text-xs font-bold">3</span>
                      </div>
                      <div>
                        <p className="font-medium text-text-primary mb-1">Build Your 28-Day Baseline</p>
                        <p className="text-sm text-text-secondary">For optimal accuracy, aim to complete 28 consecutive days of data entry. The system becomes more personalized and accurate with each day of data. However, it will start providing useful insights immediately.</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-white text-xs font-bold">4</span>
                      </div>
                      <div>
                        <p className="font-medium text-text-primary mb-1">Check Your Dashboard Before Training</p>
                        <p className="text-sm text-text-secondary">Review your Readiness Index each morning to understand whether today is a good day to push hard or a day to prioritize recovery.</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-accent/10 border border-accent/30 rounded-lg p-4">
                  <h4 className="font-semibold text-text-primary mb-3">Sample Readiness Recommendations</h4>
                  <p className="text-sm text-text-secondary mb-4">
                    Based on your combined load and wellness state, you'll receive specific guidance like these examples:
                  </p>
                  <div className="space-y-3">
                    <div className="bg-background rounded p-3">
                      <p className="text-xs font-mono text-success mb-1">GREEN BAND EXAMPLE:</p>
                      <p className="text-sm text-text-secondary">"Load and wellness are in line with your normal. Proceed with the planned session."</p>
                    </div>
                    <div className="bg-background rounded p-3">
                      <p className="text-xs font-mono text-warning mb-1">AMBER BAND EXAMPLE:</p>
                      <p className="text-sm text-text-secondary">"Your training load is typical, but you are reporting lower wellness than normal. Consider reducing the intensity or volume today and prioritize recovery."</p>
                    </div>
                    <div className="bg-background rounded p-3">
                      <p className="text-xs font-mono text-error mb-1">RED BAND EXAMPLE:</p>
                      <p className="text-sm text-text-secondary">"You are training harder than usual while feeling worse than normal. Strongly consider reducing today's load and emphasize recovery."</p>
                    </div>
                  </div>
                </div>

                <div className="bg-surface border border-border rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <Icon name="Info" size={20} color="var(--color-primary)" className="mt-0.5 flex-shrink-0" strokeWidth={2} />
                    <div>
                      <h5 className="font-medium text-text-primary mb-2">Important Note About Functionality</h5>
                      <p className="text-sm text-text-secondary mb-2">
                        The Readiness Index becomes fully functional and most accurate after you've logged 28 days of consistent training and wellness data. This period allows the system to:
                      </p>
                      <ul className="text-sm text-text-secondary space-y-1">
                        <li>• Establish your personal training capacity baseline</li>
                        <li>• Identify your normal wellness patterns</li>
                        <li>• Calculate accurate deviations from your norms</li>
                        <li>• Provide truly personalized recommendations</li>
                      </ul>
                      <p className="text-sm text-text-secondary mt-3">
                        <strong>However,</strong> the platform will still provide useful guidance with less data - it just becomes increasingly accurate and personalized as your data history grows. Don't wait for 28 days to start using it!
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </ContentSection>

            <ContentSection id="wellness-components" title="Wellness Components" icon="Activity">
              <div className="grid md:grid-cols-2 gap-6">
                {[
                {
                  component: 'Sleep Quality',
                  icon: 'Moon',
                  color: '#6366F1',
                  description: 'Assesses the quality and restfulness of your previous night\'s sleep, which is fundamental to recovery and adaptation.',
                  factors: [
                  'Duration of sleep (7-9 hours optimal)',
                  'Sleep onset time and wake frequency',
                  'Feeling of restfulness upon waking',
                  'Sleep environment quality'],

                  tips: [
                  'Maintain consistent sleep schedule',
                  'Create optimal sleep environment',
                  'Limit screen time before bed',
                  'Consider sleep hygiene practices']

                },
                {
                  component: 'Fatigue Level',
                  icon: 'Battery',
                  color: '#EF4444',
                  description: 'Measures your current energy levels and overall tiredness, reflecting your body\'s recovery status.',
                  factors: [
                  'Physical energy and alertness',
                  'Mental clarity and focus',
                  'Motivation for daily activities',
                  'Overall vitality feeling'],

                  tips: [
                  'Monitor patterns over time',
                  'Consider nutrition and hydration',
                  'Assess training load correlation',
                  'Note external stressors']

                },
                {
                  component: 'Muscle Soreness',
                  icon: 'Zap',
                  color: '#F59E0B',
                  description: 'Evaluates the level of muscle soreness and stiffness, indicating mechanical stress and recovery needs.',
                  factors: [
                  'General muscle stiffness',
                  'Specific area soreness',
                  'Movement quality and range',
                  'Pain vs normal adaptation'],

                  tips: [
                  'Distinguish between good and bad pain',
                  'Consider recovery modalities',
                  'Monitor progression over days',
                  'Note asymmetries or specific areas']

                },
                {
                  component: 'Stress Level',
                  icon: 'Brain',
                  color: '#8B5CF6',
                  description: 'Assesses psychological stress from training, work, and life factors that impact recovery and performance.',
                  factors: [
                  'Work and life pressures',
                  'Training-related anxiety',
                  'Emotional state and mood',
                  'Coping capacity feeling'],

                  tips: [
                  'Include all stress sources',
                  'Practice stress management',
                  'Consider meditation or relaxation',
                  'Seek support when needed']

                }]?.
                map((item, index) =>
                <div key={index} className="bg-surface border border-border rounded-lg p-6">
                    <div className="flex items-center space-x-3 mb-4">
                      <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: item?.color }}>

                        <Icon name={item?.icon} size={20} color="white" strokeWidth={2} />
                      </div>
                      <h4 className="text-lg font-semibold text-text-primary">{item?.component}</h4>
                    </div>
                    
                    <p className="text-text-secondary mb-4">{item?.description}</p>
                    
                    <div className="space-y-4">
                      <div>
                        <h5 className="font-medium text-text-primary mb-2">Key Factors:</h5>
                        <ul className="text-sm text-text-secondary space-y-1">
                          {item?.factors?.map((factor, factorIndex) =>
                        <li key={factorIndex} className="flex items-start space-x-2">
                              <Icon name="Dot" size={12} color="currentColor" className="mt-1 flex-shrink-0" />
                              <span>{factor}</span>
                            </li>
                        )}
                        </ul>
                      </div>
                      
                      <div>
                        <h5 className="font-medium text-text-primary mb-2">Monitoring Tips:</h5>
                        <ul className="text-sm text-text-secondary space-y-1">
                          {item?.tips?.map((tip, tipIndex) =>
                        <li key={tipIndex} className="flex items-start space-x-2">
                              <Icon name="Lightbulb" size={12} color="var(--color-warning)" className="mt-1 flex-shrink-0" />
                              <span>{tip}</span>
                            </li>
                        )}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </ContentSection>

            <ContentSection id="tracking-tips" title="Effective Tracking" icon="Target">
              <div className="space-y-6">
                <ExpandableCard
                  title="Consistency is Key"
                  summary="Establish daily routines for accurate wellness monitoring"
                  icon="Clock"
                  defaultExpanded={true}>

                  <div className="space-y-4">
                    <p className="text-text-secondary">
                      Consistent timing and conditions for wellness assessments ensure reliable data that accurately reflects your body's patterns and responses to training.
                    </p>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-muted rounded-lg p-4">
                        <h5 className="font-medium text-text-primary mb-3">Optimal Timing</h5>
                        <ul className="text-sm text-text-secondary space-y-2">
                          <li className="flex items-start space-x-2">
                            <Icon name="Sun" size={14} color="var(--color-warning)" className="mt-0.5 flex-shrink-0" />
                            <span>Same time each morning (within 30 minutes)</span>
                          </li>
                          <li className="flex items-start space-x-2">
                            <Icon name="Coffee" size={14} color="var(--color-accent)" className="mt-0.5 flex-shrink-0" />
                            <span>Before caffeine or significant activity</span>
                          </li>
                          <li className="flex items-start space-x-2">
                            <Icon name="Smartphone" size={14} color="var(--color-primary)" className="mt-0.5 flex-shrink-0" />
                            <span>Use phone reminders or alarms</span>
                          </li>
                        </ul>
                      </div>
                      
                      <div className="bg-muted rounded-lg p-4">
                        <h5 className="font-medium text-text-primary mb-3">Environmental Factors</h5>
                        <ul className="text-sm text-text-secondary space-y-2">
                          <li className="flex items-start space-x-2">
                            <Icon name="Home" size={14} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                            <span>Quiet, comfortable environment</span>
                          </li>
                          <li className="flex items-start space-x-2">
                            <Icon name="Thermometer" size={14} color="var(--color-error)" className="mt-0.5 flex-shrink-0" />
                            <span>Consistent room temperature</span>
                          </li>
                          <li className="flex items-start space-x-2">
                            <Icon name="Volume2" size={14} color="var(--color-text-secondary)" className="mt-0.5 flex-shrink-0" />
                            <span>Minimal distractions or noise</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </ExpandableCard>

                <ExpandableCard
                  title="Honest Self-Assessment"
                  summary="Guidelines for accurate and objective wellness ratings"
                  icon="Eye">

                  <div className="space-y-4">
                    <p className="text-text-secondary">
                      Honest self-assessment is crucial for meaningful wellness data. Avoid the temptation to rate based on training plans or expectations.
                    </p>
                    
                    <div className="bg-surface border border-border rounded-lg p-4">
                      <h5 className="font-medium text-text-primary mb-3">Assessment Guidelines</h5>
                      <div className="space-y-3">
                        <div className="flex items-start space-x-3">
                          <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            <span className="text-white text-xs font-bold">1</span>
                          </div>
                          <div>
                            <p className="font-medium text-text-primary">Rate how you feel, not how you think you should feel</p>
                            <p className="text-sm text-text-secondary">Base ratings on actual sensations, not training expectations</p>
                          </div>
                        </div>
                        
                        <div className="flex items-start space-x-3">
                          <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            <span className="text-white text-xs font-bold">2</span>
                          </div>
                          <div>
                            <p className="font-medium text-text-primary">Use the full scale range</p>
                            <p className="text-sm text-text-secondary">Don't avoid extreme ratings when they accurately reflect your state</p>
                          </div>
                        </div>
                        
                        <div className="flex items-start space-x-3">
                          <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            <span className="text-white text-xs font-bold">3</span>
                          </div>
                          <div>
                            <p className="font-medium text-text-primary">Consider your personal baseline</p>
                            <p className="text-sm text-text-secondary">Rate relative to your normal state, not others' experiences</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </ExpandableCard>

                <ExpandableCard
                  title="Pattern Recognition"
                  summary="Learning to identify trends and correlations in your data"
                  icon="TrendingUp">

                  <div className="space-y-4">
                    <p className="text-text-secondary">
                      Over time, patterns will emerge in your wellness data that provide valuable insights into your body's responses and optimal training approaches.
                    </p>
                    
                    <div className="grid gap-4">
                      <div className="bg-muted rounded-lg p-4">
                        <h5 className="font-medium text-text-primary mb-2">Weekly Patterns</h5>
                        <p className="text-sm text-text-secondary mb-3">
                          Look for consistent weekly trends that may relate to training structure, work schedules, or lifestyle factors.
                        </p>
                        <div className="text-xs bg-surface border border-border rounded p-2">
                          Example: Lower wellness scores on Mondays may indicate insufficient weekend recovery
                        </div>
                      </div>
                      
                      <div className="bg-muted rounded-lg p-4">
                        <h5 className="font-medium text-text-primary mb-2">Training Correlations</h5>
                        <p className="text-sm text-text-secondary mb-3">
                          Observe how wellness scores relate to training load, intensity, and recovery periods.
                        </p>
                        <div className="text-xs bg-surface border border-border rounded p-2">
                          Example: Consistent drops in sleep quality after high-intensity sessions
                        </div>
                      </div>
                      
                      <div className="bg-muted rounded-lg p-4">
                        <h5 className="font-medium text-text-primary mb-2">External Factors</h5>
                        <p className="text-sm text-text-secondary mb-3">
                          Note how life stressors, travel, nutrition, and other factors impact your wellness metrics.
                        </p>
                        <div className="text-xs bg-surface border border-border rounded p-2">
                          Example: Elevated stress scores during work deadlines affecting overall wellness
                        </div>
                      </div>
                    </div>
                  </div>
                </ExpandableCard>
              </div>
            </ContentSection>

            {/* Maturation Monitoring Section */}
            <ContentSection id="maturation-overview" title="Growth Tracking Overview" icon="Users">
              <div className="space-y-6">
                <p className="text-text-secondary leading-relaxed">
                  The Maturation Monitor is a specialized tool designed for parents, guardians, and coaches to track youth athlete growth and development. By monitoring height and growth velocity over time, you can identify key developmental milestones like Peak Height Velocity (PHV) and adjust training loads accordingly during critical growth periods.
                </p>

                <div className="bg-primary/10 border border-primary/30 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <Icon name="Info" size={20} color="var(--color-primary)" className="mt-0.5 flex-shrink-0" strokeWidth={2} />
                    <div>
                      <h4 className="font-semibold text-text-primary mb-1">Why Track Growth in Young Athletes?</h4>
                      <p className="text-sm text-text-secondary mb-2">
                        Adolescent growth spurts significantly impact injury risk, training capacity, and performance. During rapid growth phases, young athletes experience temporary changes in coordination, strength, and flexibility that require careful training adjustments.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-muted rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <Icon name="TrendingUp" size={20} color="var(--color-primary)" strokeWidth={2} />
                      <h4 className="font-semibold text-text-primary">Height Tracking</h4>
                    </div>
                    <p className="text-sm text-text-secondary">
                      Regular height measurements (every 3 months recommended) create a growth curve that reveals developmental patterns and identifies growth spurts.
                    </p>
                  </div>

                  <div className="bg-muted rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <Icon name="Activity" size={20} color="var(--color-accent)" strokeWidth={2} />
                      <h4 className="font-semibold text-text-primary">Growth Velocity</h4>
                    </div>
                    <p className="text-sm text-text-secondary">
                      Calculated automatically from your measurements, growth velocity (cm/year) shows the rate of growth and helps identify Peak Height Velocity timing.
                    </p>
                  </div>

                  <div className="bg-muted rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <Icon name="Target" size={20} color="var(--color-success)" strokeWidth={2} />
                      <h4 className="font-semibold text-text-primary">PHV Detection</h4>
                    </div>
                    <p className="text-sm text-text-secondary">
                      The system identifies when a young athlete reaches Peak Height Velocity, a critical milestone for training load management and injury prevention.
                    </p>
                  </div>
                </div>

                <ExpandableCard
                  title="Getting Started with Maturation Monitoring"
                  summary="Step-by-step guide to setting up growth tracking"
                  icon="Play"
                  defaultExpanded={true}>
                  <div className="space-y-4">
                    <div className="space-y-3">
                      <div className="flex items-start space-x-3">
                        <div className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold flex-shrink-0">1</div>
                        <div>
                          <h5 className="font-medium text-text-primary mb-1">Accept Consent & Privacy Terms</h5>
                          <p className="text-sm text-text-secondary">
                            Review and accept the data privacy consent form. All growth data is stored securely and only accessible to the guardian account.
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start space-x-3">
                        <div className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold flex-shrink-0">2</div>
                        <div>
                          <h5 className="font-medium text-text-primary mb-1">Create Child Profile</h5>
                          <p className="text-sm text-text-secondary">
                            Enter basic information: child's name, date of birth, and biological sex (required for accurate growth percentile calculations).
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start space-x-3">
                        <div className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold flex-shrink-0">3</div>
                        <div>
                          <h5 className="font-medium text-text-primary mb-1">Record First Measurement</h5>
                          <p className="text-sm text-text-secondary">
                            Take an accurate standing height measurement and enter it with the measurement date. Follow the measurement guidance for best accuracy.
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start space-x-3">
                        <div className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold flex-shrink-0">4</div>
                        <div>
                          <h5 className="font-medium text-text-primary mb-1">Set Measurement Reminders</h5>
                          <p className="text-sm text-text-secondary">
                            Configure email reminders for regular measurements (recommended every 3 months). Consistent tracking is key to accurate growth velocity calculations.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-accent/10 border border-accent/30 rounded-lg p-4 mt-4">
                      <div className="flex items-start space-x-2">
                        <Icon name="Calendar" size={18} color="var(--color-accent)" className="mt-0.5" />
                        <div>
                          <h5 className="font-medium text-text-primary mb-1">Measurement Frequency</h5>
                          <p className="text-sm text-text-secondary">
                            For optimal growth tracking, measure height every 3 months. More frequent measurements (monthly) provide better growth velocity accuracy during rapid growth phases.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </ExpandableCard>
              </div>
            </ContentSection>

            <ContentSection id="phv-explained" title="Peak Height Velocity (PHV)" icon="TrendingUp">
              <div className="space-y-6">
                <p className="text-text-secondary leading-relaxed">
                  Peak Height Velocity (PHV) is the period of maximum growth rate during adolescence. Identifying PHV timing is crucial for youth athlete development because this growth spurt significantly affects training capacity, injury risk, and performance.
                </p>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-surface border border-border rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-3">
                      <Icon name="Users" size={20} color="var(--color-primary)" strokeWidth={2} />
                      <h4 className="font-semibold text-text-primary">Typical PHV Timing</h4>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm font-medium text-text-primary mb-1">Girls:</p>
                        <p className="text-sm text-text-secondary">
                          Typically occurs around age 11-12 years, with growth rates reaching 8-9 cm/year at peak.
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-text-primary mb-1">Boys:</p>
                        <p className="text-sm text-text-secondary">
                          Typically occurs around age 13-14 years, with growth rates reaching 9-10 cm/year at peak.
                        </p>
                      </div>
                      <div className="bg-muted rounded p-3 mt-3">
                        <p className="text-xs text-text-secondary">
                          <Icon name="Info" size={12} color="var(--color-primary)" className="inline mr-1" />
                          <strong>Note:</strong> Individual variation is significant. Some athletes reach PHV earlier or later than average.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-surface border border-border rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-3">
                      <Icon name="AlertTriangle" size={20} color="var(--color-warning)" strokeWidth={2} />
                      <h4 className="font-semibold text-text-primary">PHV Impact on Training</h4>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-start space-x-2">
                        <Icon name="AlertCircle" size={14} color="var(--color-warning)" className="mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-text-secondary">
                          <strong>Increased Injury Risk:</strong> Rapid bone growth temporarily outpaces muscle/tendon adaptation
                        </p>
                      </div>
                      <div className="flex items-start space-x-2">
                        <Icon name="AlertCircle" size={14} color="var(--color-warning)" className="mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-text-secondary">
                          <strong>Coordination Changes:</strong> Body proportions shift, affecting movement patterns and technique
                        </p>
                      </div>
                      <div className="flex items-start space-x-2">
                        <Icon name="AlertCircle" size={14} color="var(--color-warning)" className="mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-text-secondary">
                          <strong>Reduced Flexibility:</strong> Temporary decrease in relative flexibility as bones lengthen
                        </p>
                      </div>
                      <div className="flex items-start space-x-2">
                        <Icon name="AlertCircle" size={14} color="var(--color-warning)" className="mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-text-secondary">
                          <strong>Training Load Sensitivity:</strong> Greater need for load management and recovery time
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <ExpandableCard
                  title="How the System Detects PHV"
                  summary="Understanding the growth velocity calculation and PHV identification"
                  icon="Calculator">
                  <div className="space-y-4">
                    <p className="text-text-secondary">
                      The platform automatically calculates growth velocity from your height measurements and identifies PHV when specific criteria are met.
                    </p>

                    <div className="bg-muted rounded-lg p-4">
                      <h5 className="font-medium text-text-primary mb-3">Growth Velocity Calculation:</h5>
                      <div className="space-y-2 font-mono text-sm">
                        <div className="bg-surface rounded p-3">
                          <p className="text-text-secondary mb-1">Height Difference:</p>
                          <p className="text-text-primary">ΔHeight = Current Height - Previous Height</p>
                        </div>
                        <div className="bg-surface rounded p-3">
                          <p className="text-text-secondary mb-1">Time Difference:</p>
                          <p className="text-text-primary">ΔTime = Days Between Measurements ÷ 365.25</p>
                        </div>
                        <div className="bg-surface rounded p-3">
                          <p className="text-text-secondary mb-1">Velocity (cm/year):</p>
                          <p className="text-text-primary">Velocity = ΔHeight ÷ ΔTime</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-primary/10 border border-primary/30 rounded-lg p-4">
                      <h5 className="font-medium text-text-primary mb-2">PHV Detection Criteria:</h5>
                      <ul className="text-sm text-text-secondary space-y-1">
                        <li>• Requires at least 3 height measurements for trend analysis</li>
                        <li>• Identifies the highest growth velocity point in the dataset</li>
                        <li>• Confirms velocity decline after the peak (validates PHV occurrence)</li>
                        <li>• Minimum velocity threshold: 6 cm/year for girls, 7 cm/year for boys</li>
                      </ul>
                    </div>

                    <div className="bg-accent/10 border border-accent/30 rounded-lg p-4">
                      <div className="flex items-start space-x-2">
                        <Icon name="Lightbulb" size={18} color="var(--color-accent)" className="mt-0.5" />
                        <div>
                          <h5 className="font-medium text-text-primary mb-1">Why Multiple Measurements Matter</h5>
                          <p className="text-sm text-text-secondary">
                            PHV can only be confirmed retrospectively after growth velocity begins to decline. This is why consistent, long-term tracking (12-24 months) is essential for accurate PHV identification.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </ExpandableCard>

                <ExpandableCard
                  title="Training Recommendations During PHV"
                  summary="How to adjust training load during rapid growth phases"
                  icon="Target">
                  <div className="space-y-4">
                    <p className="text-text-secondary">
                      When the system detects PHV or rapid growth (velocity &gt; 6 cm/year), specific training modifications are recommended to reduce injury risk while maintaining development.
                    </p>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-success/10 border border-success/30 rounded-lg p-4">
                        <h5 className="font-medium text-text-primary mb-2 flex items-center space-x-2">
                          <Icon name="CheckCircle" size={16} color="var(--color-success)" />
                          <span>Recommended Practices</span>
                        </h5>
                        <ul className="text-sm text-text-secondary space-y-1">
                          <li>• Reduce training volume by 10-20%</li>
                          <li>• Increase recovery time between sessions</li>
                          <li>• Focus on technique and skill development</li>
                          <li>• Emphasize flexibility and mobility work</li>
                          <li>• Monitor wellness scores more closely</li>
                          <li>• Prioritize sleep and nutrition</li>
                        </ul>
                      </div>

                      <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4">
                        <h5 className="font-medium text-text-primary mb-2 flex items-center space-x-2">
                          <Icon name="XCircle" size={16} color="var(--color-destructive)" />
                          <span>Avoid During PHV</span>
                        </h5>
                        <ul className="text-sm text-text-secondary space-y-1">
                          <li>• Sudden increases in training load</li>
                          <li>• High-volume plyometric training</li>
                          <li>• Maximal strength testing</li>
                          <li>• Excessive competition frequency</li>
                          <li>• Training through pain or discomfort</li>
                          <li>• Ignoring signs of overtraining</li>
                        </ul>
                      </div>
                    </div>

                    <div className="bg-muted rounded-lg p-4">
                      <h5 className="font-medium text-text-primary mb-2">Integration with Training Load Monitoring</h5>
                      <p className="text-sm text-text-secondary mb-3">
                        The Maturation Monitor works alongside your training load tracking. During identified PHV periods, the dashboard may show additional warnings if Load Ratio enters moderate or high-risk zones.
                      </p>
                      <p className="text-sm text-text-secondary">
                        <strong>Key Principle:</strong> Young athletes in PHV should maintain Load Ratios in the lower end of the optimal zone (0.8-1.0) rather than the upper end (1.1-1.3) to account for growth-related injury risk.
                      </p>
                    </div>
                  </div>
                </ExpandableCard>
              </div>
            </ContentSection>

            <ContentSection id="measurement-guidance" title="Measurement Guidelines" icon="Ruler">
              <div className="space-y-6">
                <p className="text-text-secondary leading-relaxed">
                  Accurate height measurements are essential for reliable growth tracking and PHV detection. Follow these standardized measurement protocols to ensure consistency and precision.
                </p>

                <ExpandableCard
                  title="How to Measure Height Accurately"
                  summary="Step-by-step protocol for consistent height measurements"
                  icon="Ruler"
                  defaultExpanded={true}>
                  <div className="space-y-4">
                    <div className="bg-primary/10 border border-primary/30 rounded-lg p-4">
                      <h5 className="font-semibold text-text-primary mb-3">Equipment Needed:</h5>
                      <ul className="text-sm text-text-secondary space-y-1">
                        <li>• Wall-mounted stadiometer (preferred) or measuring tape</li>
                        <li>• Flat, hard surface (not carpet)</li>
                        <li>• Right-angle tool or book for head positioning</li>
                        <li>• Mirror (optional, for self-measurement verification)</li>
                      </ul>
                    </div>

                    <div className="space-y-3">
                      <h5 className="font-medium text-text-primary mb-2">Measurement Protocol:</h5>
                      
                      <div className="flex items-start space-x-3">
                        <div className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold flex-shrink-0">1</div>
                        <div>
                          <h6 className="font-medium text-text-primary mb-1">Remove Shoes and Hair Accessories</h6>
                          <p className="text-sm text-text-secondary">
                            Measure in bare feet or thin socks. Remove any hair ties, clips, or headwear that could affect measurement.
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start space-x-3">
                        <div className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold flex-shrink-0">2</div>
                        <div>
                          <h6 className="font-medium text-text-primary mb-1">Stand Against Wall</h6>
                          <p className="text-sm text-text-secondary">
                            Stand with heels, buttocks, shoulders, and back of head touching the wall. Feet should be together and flat on the floor.
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start space-x-3">
                        <div className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold flex-shrink-0">3</div>
                        <div>
                          <h6 className="font-medium text-text-primary mb-1">Position Head Correctly</h6>
                          <p className="text-sm text-text-secondary">
                            Look straight ahead with eyes level (Frankfurt plane: imaginary line from ear canal to lower eye socket should be horizontal). Chin should be parallel to the floor.
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start space-x-3">
                        <div className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold flex-shrink-0">4</div>
                        <div>
                          <h6 className="font-medium text-text-primary mb-1">Take Deep Breath</h6>
                          <p className="text-sm text-text-secondary">
                            Inhale deeply and stand as tall as possible without lifting heels. This ensures maximum height measurement.
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start space-x-3">
                        <div className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold flex-shrink-0">5</div>
                        <div>
                          <h6 className="font-medium text-text-primary mb-1">Mark and Measure</h6>
                          <p className="text-sm text-text-secondary">
                            Place a flat object (book or ruler) on top of the head at a 90-degree angle to the wall. Mark the position and measure from floor to mark.
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start space-x-3">
                        <div className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold flex-shrink-0">6</div>
                        <div>
                          <h6 className="font-medium text-text-primary mb-1">Record Immediately</h6>
                          <p className="text-sm text-text-secondary">
                            Enter the measurement into the system right away to avoid transcription errors. Record to the nearest 0.1 cm or 0.5 cm.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-warning/10 border border-warning/30 rounded-lg p-4 mt-4">
                      <div className="flex items-start space-x-2">
                        <Icon name="AlertTriangle" size={18} color="var(--color-warning)" className="mt-0.5" />
                        <div>
                          <h5 className="font-medium text-text-primary mb-1">Common Measurement Errors to Avoid</h5>
                          <ul className="text-sm text-text-secondary space-y-1">
                            <li>• Measuring on carpet (adds height)</li>
                            <li>• Not removing shoes or thick socks</li>
                            <li>• Head tilted up or down</li>
                            <li>• Heels lifted off the ground</li>
                            <li>• Measuring at different times of day (height varies by ~1-2 cm)</li>
                            <li>• Using different measurement tools between sessions</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </ExpandableCard>

                <ExpandableCard
                  title="Measurement Timing and Frequency"
                  summary="When and how often to measure for optimal tracking"
                  icon="Calendar">
                  <div className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-surface border border-border rounded-lg p-4">
                        <h5 className="font-medium text-text-primary mb-2">Recommended Frequency</h5>
                        <div className="space-y-2 text-sm text-text-secondary">
                          <p><strong>Standard Tracking:</strong> Every 3 months (quarterly)</p>
                          <p><strong>During Growth Spurts:</strong> Every 1-2 months</p>
                          <p><strong>Minimum for PHV Detection:</strong> Every 6 months</p>
                        </div>
                        <div className="bg-muted rounded p-3 mt-3">
                          <p className="text-xs text-text-secondary">
                            <Icon name="Info" size={12} color="var(--color-primary)" className="inline mr-1" />
                            More frequent measurements provide better growth velocity accuracy but aren't necessary for all athletes.
                          </p>
                        </div>
                      </div>

                      <div className="bg-surface border border-border rounded-lg p-4">
                        <h5 className="font-medium text-text-primary mb-2">Best Time to Measure</h5>
                        <div className="space-y-2 text-sm text-text-secondary">
                          <p><strong>Time of Day:</strong> Morning (height is greatest after sleep)</p>
                          <p><strong>Consistency:</strong> Same time of day for all measurements</p>
                          <p><strong>Avoid After:</strong> Heavy training or prolonged standing (spine compression)</p>
                        </div>
                        <div className="bg-muted rounded p-3 mt-3">
                          <p className="text-xs text-text-secondary">
                            <Icon name="Clock" size={12} color="var(--color-primary)" className="inline mr-1" />
                            Height can vary by 1-2 cm throughout the day due to spinal compression.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-accent/10 border border-accent/30 rounded-lg p-4">
                      <h5 className="font-medium text-text-primary mb-2">Setting Up Measurement Reminders</h5>
                      <p className="text-sm text-text-secondary mb-3">
                        The platform includes an automated reminder system to help you maintain consistent measurement schedules.
                      </p>
                      <ul className="text-sm text-text-secondary space-y-1">
                        <li>• Configure reminder frequency in Settings (1, 2, 3, or 6 months)</li>
                        <li>• Receive email notifications when measurements are due</li>
                        <li>• Reminders include measurement guidance and direct link to entry form</li>
                        <li>• Adjust reminder schedule anytime based on growth patterns</li>
                      </ul>
                    </div>
                  </div>
                </ExpandableCard>

                <div className="bg-muted rounded-lg p-4">
                  <h5 className="font-medium text-text-primary mb-2">Measurement Accuracy Tips</h5>
                  <div className="grid md:grid-cols-2 gap-4 text-sm text-text-secondary">
                    <div>
                      <p className="font-medium text-text-primary mb-1">For Best Results:</p>
                      <ul className="space-y-1">
                        <li>• Use the same measurement location each time</li>
                        <li>• Have the same person take measurements when possible</li>
                        <li>• Measure twice and average if uncertain</li>
                        <li>• Record measurements immediately to avoid errors</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-medium text-text-primary mb-1">Quality Control:</p>
                      <ul className="space-y-1">
                        <li>• System flags unusual measurements (e.g., height decrease)</li>
                        <li>• Review flagged entries before confirming</li>
                        <li>• Can edit or delete incorrect measurements</li>
                        <li>• Growth velocity recalculates automatically after edits</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </ContentSection>

            <ContentSection id="growth-insights" title="Growth Insights & Reporting" icon="BarChart3">
              <div className="space-y-6">
                <p className="text-text-secondary leading-relaxed">
                  The Maturation Monitor provides comprehensive visualizations and insights to help you understand growth patterns, identify developmental milestones, and make informed training decisions.
                </p>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-surface border border-border rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-3">
                      <Icon name="TrendingUp" size={20} color="var(--color-primary)" strokeWidth={2} />
                      <h4 className="font-semibold text-text-primary">Height Growth Chart</h4>
                    </div>
                    <p className="text-sm text-text-secondary mb-3">
                      Visualizes height measurements over time with age-based percentile curves (CDC growth standards). Shows where the athlete falls relative to population norms.
                    </p>
                    <div className="bg-muted rounded p-3">
                      <p className="text-xs text-text-secondary">
                        <strong>What You See:</strong> Individual height points plotted against 5th, 25th, 50th, 75th, and 95th percentile curves for age and sex.
                      </p>
                    </div>
                  </div>

                  <div className="bg-surface border border-border rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-3">
                      <Icon name="Activity" size={20} color="var(--color-accent)" strokeWidth={2} />
                      <h4 className="font-semibold text-text-primary">Growth Velocity Chart</h4>
                    </div>
                    <p className="text-sm text-text-secondary mb-3">
                      Displays growth rate (cm/year) between measurements. The most important chart for PHV detection, showing when growth accelerates and decelerates.
                    </p>
                    <div className="bg-muted rounded p-3">
                      <p className="text-xs text-text-secondary">
                        <strong>What You See:</strong> Line graph of growth velocity over time with PHV marker (if detected) and typical velocity ranges highlighted.
                      </p>
                    </div>
                  </div>
                </div>

                <ExpandableCard
                  title="Understanding Growth Percentiles"
                  summary="How to interpret percentile curves and what they mean"
                  icon="Info">
                  <div className="space-y-4">
                    <p className="text-text-secondary">
                      Growth percentiles compare an individual's height to population standards based on age and biological sex. These curves help identify normal growth patterns and potential concerns.
                    </p>

                    <div className="bg-muted rounded-lg p-4">
                      <h5 className="font-medium text-text-primary mb-3">Percentile Interpretation:</h5>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between items-center">
                          <span className="text-text-secondary">95th Percentile:</span>
                          <span className="text-text-primary">Taller than 95% of peers</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-text-secondary">75th Percentile:</span>
                          <span className="text-text-primary">Taller than 75% of peers</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-text-secondary">50th Percentile (Median):</span>
                          <span className="text-text-primary">Average height for age</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-text-secondary">25th Percentile:</span>
                          <span className="text-text-primary">Taller than 25% of peers</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-text-secondary">5th Percentile:</span>
                          <span className="text-text-primary">Taller than 5% of peers</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-primary/10 border border-primary/30 rounded-lg p-4">
                      <h5 className="font-medium text-text-primary mb-2">Key Points About Percentiles:</h5>
                      <ul className="text-sm text-text-secondary space-y-1">
                        <li>• <strong>Percentile tracking matters more than absolute percentile:</strong> Consistent growth along a percentile curve is normal</li>
                        <li>• <strong>Crossing percentiles:</strong> Significant upward or downward movement may indicate growth spurt or concern</li>
                        <li>• <strong>Genetic factors:</strong> Children of tall parents typically track higher percentiles</li>
                        <li>• <strong>Late bloomers:</strong> Some athletes grow later than peers (normal variation)</li>
                      </ul>
                    </div>

                    <div className="bg-warning/10 border border-warning/30 rounded-lg p-4">
                      <div className="flex items-start space-x-2">
                        <Icon name="AlertTriangle" size={18} color="var(--color-warning)" className="mt-0.5" />
                        <div>
                          <h5 className="font-medium text-text-primary mb-1">When to Consult a Healthcare Provider</h5>
                          <ul className="text-sm text-text-secondary space-y-1">
                            <li>• Sudden drop across two or more percentile curves</li>
                            <li>• Growth velocity consistently below 4 cm/year after age 4</li>
                            <li>• Height below 3rd percentile or above 97th percentile</li>
                            <li>• Concerns about early or delayed puberty</li>
                          </ul>
                          <p className="text-xs text-text-secondary mt-2">
                            <strong>Note:</strong> This tool is for athletic development tracking, not medical diagnosis.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </ExpandableCard>

                <ExpandableCard
                  title="Growth Insights & PDF Reports"
                  summary="Comprehensive reporting features for coaches and parents"
                  icon="FileText">
                  <div className="space-y-4">
                    <p className="text-text-secondary">
                      The platform generates detailed growth insights and exportable PDF reports for sharing with coaches, healthcare providers, or keeping personal records.
                    </p>

                    <div className="bg-surface border border-border rounded-lg p-4">
                      <h5 className="font-medium text-text-primary mb-3">Insights Dashboard Features:</h5>
                      <div className="grid md:grid-cols-2 gap-3 text-sm">
                        <div className="flex items-start space-x-2">
                          <Icon name="CheckCircle" size={14} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                          <span className="text-text-secondary">Current height and percentile ranking</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <Icon name="CheckCircle" size={14} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                          <span className="text-text-secondary">Latest growth velocity calculation</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <Icon name="CheckCircle" size={14} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                          <span className="text-text-secondary">PHV status and timing (if detected)</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <Icon name="CheckCircle" size={14} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                          <span className="text-text-secondary">Growth pattern analysis and trends</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <Icon name="CheckCircle" size={14} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                          <span className="text-text-secondary">Training recommendations based on growth phase</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <Icon name="CheckCircle" size={14} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                          <span className="text-text-secondary">Next measurement due date reminder</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-accent/10 border border-accent/30 rounded-lg p-4">
                      <h5 className="font-medium text-text-primary mb-2">PDF Report Contents:</h5>
                      <p className="text-sm text-text-secondary mb-3">
                        Generate comprehensive PDF reports with a single click. Reports include:
                      </p>
                      <ul className="text-sm text-text-secondary space-y-1">
                        <li>• Child profile information (name, age, sex)</li>
                        <li>• Complete measurement history table</li>
                        <li>• Height growth chart with percentile curves</li>
                        <li>• Growth velocity chart with PHV markers</li>
                        <li>• Current growth status summary</li>
                        <li>• Training recommendations for current growth phase</li>
                        <li>• Report generation date and data timeframe</li>
                      </ul>
                    </div>

                    <div className="bg-muted rounded-lg p-4">
                      <h5 className="font-medium text-text-primary mb-2">Use Cases for PDF Reports:</h5>
                      <div className="grid md:grid-cols-2 gap-3 text-sm text-text-secondary">
                        <div>
                          <p className="font-medium text-text-primary mb-1">For Coaches:</p>
                          <ul className="space-y-1">
                            <li>• Inform training load decisions</li>
                            <li>• Document athlete development</li>
                            <li>• Share with sports science staff</li>
                            <li>• Support talent identification</li>
                          </ul>
                        </div>
                        <div>
                          <p className="font-medium text-text-primary mb-1">For Parents/Guardians:</p>
                          <ul className="space-y-1">
                            <li>• Share with pediatricians</li>
                            <li>• Track sibling comparisons</li>
                            <li>• Keep personal health records</li>
                            <li>• Support school sports participation</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </ExpandableCard>

                <div className="bg-primary/10 border border-primary/30 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <Icon name="Shield" size={20} color="var(--color-primary)" className="mt-0.5 flex-shrink-0" strokeWidth={2} />
                    <div>
                      <h4 className="font-semibold text-text-primary mb-2">Data Privacy & Security</h4>
                      <p className="text-sm text-text-secondary mb-2">
                        All growth data is stored securely and encrypted. Only the guardian account has access to child profiles and measurements. Data is never shared with third parties without explicit consent.
                      </p>
                      <ul className="text-sm text-text-secondary space-y-1">
                        <li>• Guardian-only access to all child data</li>
                        <li>• Secure cloud storage with encryption</li>
                        <li>• Option to delete profiles and all associated data</li>
                        <li>• GDPR and data protection compliant</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </ContentSection>

            <ContentSection id="recovery-strategies" title="Recovery Strategies" icon="RefreshCw">
              <div className="space-y-6">
                <p className="text-text-secondary leading-relaxed">
                  Recovery is where adaptation occurs. Use your wellness data and load ratios to implement targeted recovery strategies that enhance your body's ability to adapt to training stress.
                </p>

                <div className="grid md:grid-cols-2 gap-6">
                  {[
                  {
                    category: 'Sleep Optimisation',
                    icon: 'Moon',
                    color: '#6366F1',
                    strategies: [
                    'Maintain consistent sleep schedule (±30 minutes)',
                    'Create optimal sleep environment (cool, dark, quiet)',
                    'Limit screen exposure 1 hour before bed',
                    'Consider sleep tracking for objective data',
                    'Address sleep issues promptly with professionals'],

                    indicators: [
                    'Sleep quality scores consistently <4',
                    'Fatigue levels elevated despite adequate rest',
                    'Difficulty falling asleep after hard training']

                  },
                  {
                    category: 'Nutrition & Hydration',
                    icon: 'Apple',
                    color: '#059669',
                    strategies: [
                    'Prioritise post-workout nutrition within 30 minutes',
                    'Maintain consistent meal timing',
                    'Monitor hydration status throughout the day',
                    'Consider anti-inflammatory foods during high load',
                    'Adjust caloric intake based on training demands'],

                    indicators: [
                    'Slow recovery between sessions',
                    'Persistent muscle soreness',
                    'Energy levels inconsistent with sleep quality']

                  },
                  {
                    category: 'Active Recovery',
                    icon: 'Activity',
                    color: '#0EA5E9',
                    strategies: [
                    'Light movement on rest days (walking, easy cycling)',
                    'Gentle stretching or yoga sessions',
                    'Low-intensity swimming or water activities',
                    'Mobility work targeting problem areas',
                    'Breathing exercises and meditation'],

                    indicators: [
                    'Stiffness and soreness on rest days',
                    'Difficulty returning to training after breaks',
                    'Elevated stress levels affecting recovery']

                  },
                  {
                    category: 'Recovery Modalities',
                    icon: 'Zap',
                    color: '#F59E0B',
                    strategies: [
                    'Regular massage or self-massage techniques',
                    'Contrast showers or ice baths when appropriate',
                    'Compression garments during travel or sleep',
                    'Sauna or heat therapy for muscle relaxation',
                    'Professional physiotherapy when needed'],

                    indicators: [
                    'Persistent muscle tension or knots',
                    'Reduced range of motion',
                    'Chronic soreness in specific areas']

                  }]?.
                  map((category, index) =>
                  <div key={index} className="bg-surface border border-border rounded-lg p-6">
                      <div className="flex items-center space-x-3 mb-4">
                        <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: category?.color }}>

                          <Icon name={category?.icon} size={20} color="white" strokeWidth={2} />
                        </div>
                        <h4 className="text-lg font-semibold text-text-primary">{category?.category}</h4>
                      </div>
                      
                      <div className="space-y-4">
                        <div>
                          <h5 className="font-medium text-text-primary mb-2">Implementation Strategies:</h5>
                          <ul className="space-y-2">
                            {category?.strategies?.map((strategy, strategyIndex) =>
                          <li key={strategyIndex} className="flex items-start space-x-2 text-sm text-text-secondary">
                                <Icon name="ArrowRight" size={14} color="currentColor" className="mt-0.5 flex-shrink-0" />
                                <span>{strategy}</span>
                              </li>
                          )}
                          </ul>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-text-primary mb-2">When to Prioritise:</h5>
                          <ul className="space-y-1">
                            {category?.indicators?.map((indicator, indicatorIndex) =>
                          <li key={indicatorIndex} className="flex items-start space-x-2 text-sm text-text-secondary">
                                <Icon name="AlertCircle" size={14} color="var(--color-warning)" className="mt-0.5 flex-shrink-0" />
                                <span>{indicator}</span>
                              </li>
                          )}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </ContentSection>

            <ContentSection id="app-features" title="Platform Features" icon="Layers">
              <div className="space-y-6">
                <p className="text-text-secondary leading-relaxed">
                  Sports Science Platform includes comprehensive features for both training load monitoring and wellness tracking, with real-time updates and data management capabilities.
                </p>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-surface border border-border rounded-lg p-6">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
                        <Icon name="BarChart3" size={24} color="white" strokeWidth={2} />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-text-primary">Dashboard Overview</h3>
                        <p className="text-sm text-text-secondary">Training load analytics and session management</p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <ul className="text-sm text-text-secondary space-y-2">
                        <li className="flex items-start space-x-2">
                          <Icon name="CheckCircle" size={16} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                          <span>Live Training Load Progression Chart with 30-day view</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <Icon name="CheckCircle" size={16} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                          <span>Real-time Load Ratio (ACWR) calculation with color-coded risk bands</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <Icon name="CheckCircle" size={16} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                          <span>Session logging form with RPE and duration tracking</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <Icon name="CheckCircle" size={16} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                          <span>Session history table with edit/delete capabilities</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <Icon name="CheckCircle" size={16} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                          <span>CSV import/export for bulk data management</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <Icon name="CheckCircle" size={16} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                          <span>Date range controls for customized analytics views</span>
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div className="bg-surface border border-border rounded-lg p-6">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-12 h-12 bg-accent rounded-lg flex items-center justify-center">
                        <Icon name="Heart" size={24} color="white" strokeWidth={2} />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-text-primary">Daily Wellness</h3>
                        <p className="text-sm text-text-secondary">Hooper Index methodology with automated insights</p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <ul className="text-sm text-text-secondary space-y-2">
                        <li className="flex items-start space-x-2">
                          <Icon name="CheckCircle" size={16} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                          <span>Interactive wellness sliders for all four Hooper components</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <Icon name="CheckCircle" size={16} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                          <span>Date navigation for historical wellness entry management</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <Icon name="CheckCircle" size={16} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                          <span>Real-time Hooper Index calculation with historical comparison</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <Icon name="CheckCircle" size={16} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                          <span>30-day wellness trend chart with pattern visualization</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <Icon name="CheckCircle" size={16} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                          <span>Automated wellness insights based on 7-day patterns</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <Icon name="CheckCircle" size={16} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                          <span>Notes field for qualitative wellness observations</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <Icon name="CheckCircle" size={16} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                          <span>Wellness history table with CSV import/export functionality</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="bg-muted rounded-lg p-6">
                  <h4 className="font-semibold text-text-primary mb-4">Data Management & Integration</h4>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="text-center">
                      <Icon name="Database" size={32} color="var(--color-primary)" className="mx-auto mb-2" />
                      <h5 className="font-medium text-text-primary mb-1">Real-time Updates</h5>
                      <p className="text-sm text-text-secondary">All charts and metrics update instantly when new data is added or existing entries are modified.</p>
                    </div>
                    <div className="text-center">
                      <Icon name="Upload" size={32} color="var(--color-accent)" className="mx-auto mb-2" />
                      <h5 className="font-medium text-text-primary mb-1">CSV Import/Export</h5>
                      <p className="text-sm text-text-secondary">Bulk data management with CSV file support for both training sessions and wellness entries.</p>
                    </div>
                    <div className="text-center">
                      <Icon name="Shield" size={32} color="var(--color-success)" className="mx-auto mb-2" />
                      <h5 className="font-medium text-text-primary mb-1">Secure Storage</h5>
                      <p className="text-sm text-text-secondary">All data is securely stored with user authentication and protected by Row Level Security policies.</p>
                    </div>
                  </div>
                </div>
              </div>
            </ContentSection>

            <div className="mt-6 pt-4 border-t border-border text-center">
              <p className="text-xs text-text-secondary">
                © {new Date()?.getFullYear()} Sports Science Platform. Evidence-based training load management for optimal performance.
              </p>
            </div>
          </div>
        </main>
      </div>
      <ContactButton />
    </div>
  );
};

export default HowItWorksPage;